import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Sparkles, ShieldCheck, Globe2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-enter min-h-[calc(100vh-4rem)] grid lg:grid-cols-2 bg-[#FAFAF7]">
      <div className="relative hidden lg:block overflow-hidden warm-bg border-r border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Sparkles className="w-10 h-10 text-[#E11D48] mb-6" />
          <h2 className="font-heading font-extrabold text-5xl tracking-tight leading-tight mb-3 text-slate-900">
            {t("auth.login.subtitle_1")}<br />{t("auth.login.subtitle_2")}
          </h2>
          <p className="text-slate-600 max-w-md leading-relaxed mb-8">{t("auth.login.subtitle_desc")}</p>
          <div className="flex flex-wrap gap-3">
            <span className="pill pill-red"><ShieldCheck className="w-3.5 h-3.5" /> Real market prices</span>
            <span className="pill pill-blue"><Globe2 className="w-3.5 h-3.5" /> 8 languages</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-wider mb-2">{t("auth.login.label")}</div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight text-slate-900 mb-8">{t("auth.login.title")}</h1>

          <label className="block mb-4">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">{t("auth.login.email")}</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@example.com" className="w-full" />
          </label>
          <label className="block mb-6">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">{t("auth.login.password")}</span>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" className="w-full" />
          </label>

          {error && <div className="text-[#E11D48] text-sm mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error">{error}</div>}

          <button type="submit" disabled={loading} data-testid="login-submit-btn" className="btn-primary w-full">
            {loading ? t("auth.login.signing_in") : t("auth.login.sign_in")}
          </button>

          <p className="text-xs text-slate-500 mt-4 text-center">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="hover:text-[#E11D48] hover:underline">
              Mot de passe oublié ?
            </Link>
          </p>

          <p className="text-sm text-slate-500 mt-6 text-center">
            {t("auth.login.no_account")}{" "}
            <Link to="/register" data-testid="login-to-register-link" className="text-[#E11D48] font-semibold hover:underline">{t("auth.login.create_one")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
